import React, { useState, useContext, useEffect, useRef } from "react";
import { StyleContext } from "../providers/StyleContextProvider";
import { RecruiterMenu } from "../components/Shared/RecruiterMenu";
import { SummaryOfCompletion } from "../components/VacancyPosting/SummaryOfCompletion";
import { AuthContext } from '../providers/AuthContextProvider';
import { LoggedRecruiter } from '../models/LoggedRecruiter';
import { useMediaQuery } from 'react-responsive';
import { BasicDetails } from "../components/VacancyPosting/BasicDetails";
import { createSearchParams, useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Footer } from "../components/Shared/Footer";
import useErrorPage from "../hooks/useErrorPage";
import { SummaryOfCompletionModel } from "../models/SummaryOfCompletionModel";
import { ProfileEntrance } from "../components/Home/ProfileEntrance";
import { Button } from "../components/Shared/Buttons/Button";
import { ReactComponent as LeftArrow } from '../assets/svg/left_arrow.svg';
import { VacancyQuestions } from "../components/VacancyPosting/VacancyQuestions";
import { CVLessQuestions } from "../components/VacancyPosting/CVLessQuestions";
import { ReplyMail } from "../components/VacancyPosting/ReplyMail";
import { ReadyToSubmit } from "../components/VacancyPosting/ReadyToSubmit";
import { TabComponentRef } from "../models/TabComponentRef";

export const VacancyPostingPage: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("vacancyPostingPage"));
    const authContext = useContext(AuthContext);
    const recruiter: LoggedRecruiter = authContext.getLoggedRecruiter();
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [activeTab, setActiveTab] = useState<string>("basicDetails");
    const [tabHistory, setTabHistory] = useState<string[]>(["basicDetails"]);
    const organizationId: number = searchParams.get("orgId") === null ? 0 : +searchParams.get("orgId")!;
    const [isCVLess, setIsCVLess] = useState<boolean>(false)
    const [currentJobId, setCurrentJobId] = useState<number | undefined>(params.jobId ? Number(params.jobId) : undefined);
    const basicDetailsRef = useRef<TabComponentRef>(null);
    const vacancyQuestionsRef = useRef<TabComponentRef>(null);
    const cvLessQuestionsRef = useRef<TabComponentRef>(null);
    const replyMailRef = useRef<TabComponentRef>(null);
    const readyToSubmitRef = useRef<TabComponentRef>(null);
    const isSubmittingRef = useRef(false);
    const [toErrorPage] = useErrorPage();
    const [completionModel, setCompletionModel] = useState<SummaryOfCompletionModel>({
        basicInfo: false,
        required: false,
        salary: false,
        description: false,
        vacancyQuestions: false,
        passScore: false,
        cvLessQuestion: false,
        service: false,
        question: false,
        replyMail: false,
        optional: false,
        submission: false,
        submit: false
    });

    const handleSummaryUpdate = (modelUpdates?: Partial<SummaryOfCompletionModel>) => {
        if (modelUpdates) {
            setCompletionModel(prev => ({ ...prev, ...modelUpdates }));
            
            if (modelUpdates.cvLessQuestion !== undefined) {
                setIsCVLess(modelUpdates.cvLessQuestion);
            }
        }
    };

    useEffect(() => {                    
        if(recruiter === null) {  
            const params = { callbackUrl: location.pathname + location.search, message: "Please login or register to post a new job." };
            navigate({
                pathname: '/recruiter/account/login',
                search: `?${createSearchParams(params)}`
            });
        } 
    }, []);

    useEffect(() => {         
        setStyles(styleContext.getComponentStyle("vacancyPostingPage"));
    }, [isMobile]);

    const handleCVLessModeChange = (isCVLess: boolean) => {
        setIsCVLess(isCVLess);
    }

    const activeTabRefMap: Record<string, React.RefObject<TabComponentRef>> = {
        basicDetails: basicDetailsRef,
        vacancyQuestions: vacancyQuestionsRef,
        cvLessQuestions: cvLessQuestionsRef,
        replyToMail: replyMailRef,
        readyToSubmit: readyToSubmitRef
    };

    const handleTabChange = (nextTab: string) => {
        setTabHistory((prev) => [...prev, nextTab]);
        setActiveTab(nextTab);
    };

    const handleJobIdUpdate = (jobId: number) => {
        setCurrentJobId(jobId);
        window.history.replaceState(null, '', `/recruiter/vacancy/addedit/${jobId}`);
    };

    const handleBack = () => {
        if (tabHistory.length > 1) {
            const newHistory = [...tabHistory];
            newHistory.pop(); 
            const previousTab = newHistory[newHistory.length - 1];
            setTabHistory(newHistory);
            setActiveTab(previousTab);
        } else {
            navigate(-1);
        }
    }

    const handleSubmit = async () => {
        if (isSubmittingRef.current) return; 
        const activeRef = activeTabRefMap[activeTab];
        isSubmittingRef.current = true;
        
        try {
            await activeRef?.current?.save();
        } catch (error) {
            toErrorPage(error);
        } finally {
            isSubmittingRef.current = false;
        }
    }

    const previewVacancy = () => {
        if (currentJobId) {
            window.open(`/recruiter/preview/${currentJobId}`, "_blank");
        }
    };

    return(
        <>
            <div style={styles.mainWrapper}>
                <div style={styles.leftWrapper}>
                    <RecruiterMenu>
                        <div style={styles.topic}>
                            {activeTab === "readyToSubmit" ? "Ready to submit" : "Create a new job"}
                        </div>
                    </RecruiterMenu>
                </div>
                <div style={styles.rightWrapper}>
                    <div style={styles.pageHeader}>
                        <div style={styles.buttonWrapper}>
                            <Button style={styles.backButton} onClick={handleBack}><LeftArrow style={styles.buttonIcon} /></Button>
                            <span style={styles.buttonTittle}>{"Back"}</span>
                        </div>
                        <div style={styles.profileWrapper}><ProfileEntrance recruiter={recruiter} /></div>
                    </div>
                    {!isMobile && <div style={styles.topic}>{activeTab === "readyToSubmit" ? "Ready to submit" : "Post a New Job"}</div>}
                    <div style={styles.innerRightWrapper}>
                        <SummaryOfCompletion styles={styles.summaryOfCompletion} model={completionModel} onSubmit={handleSubmit} onPreview={previewVacancy} isSubmitting={isSubmittingRef.current}/>
                        {activeTab === "basicDetails" && <BasicDetails ref={basicDetailsRef} styles={styles.basicDetails} onUpdateSummary={handleSummaryUpdate} onTabChange={handleTabChange} onJobIdUpdate={handleJobIdUpdate} onCVLessModeChange={handleCVLessModeChange} jobId={currentJobId} orgId={organizationId}/>}
                        {activeTab === "vacancyQuestions" && <VacancyQuestions ref={vacancyQuestionsRef} styles={styles.vacancyQuestion} onUpdateSummary={handleSummaryUpdate} onTabChange={handleTabChange} jobId={currentJobId}/>}
                        {activeTab === "cvLessQuestions" && <CVLessQuestions ref={cvLessQuestionsRef} styles={styles.cvLessQuestion} onUpdateSummary={handleSummaryUpdate} onTabChange={handleTabChange} jobId={currentJobId}/>}
                        {activeTab === "replyToMail" && <ReplyMail ref={replyMailRef} styles={styles.replyMail} onUpdateSummary={handleSummaryUpdate} onTabChange={handleTabChange} jobId={currentJobId} isCVLess={isCVLess}/>}
                        {activeTab === "readyToSubmit" &&  <ReadyToSubmit ref={readyToSubmitRef} styles={styles} onUpdateSummary={handleSummaryUpdate} onTabChange={handleTabChange} onJobIdUpdate={handleJobIdUpdate} onCVLessModeChange={handleCVLessModeChange} jobId={currentJobId} isCVLess={isCVLess} onPreview={previewVacancy}/>}
                    </div>
                </div>
            </div>
            <Footer/>
        </>    
    )
}
